// Apps, connections, and environments (data-model.md "Apps and connections").
//
// A representative core of the app record: identity, tenancy, the closed-set enums
// that are contract-critical, and the spend ceiling as integer minor units. The deep
// jsonb profiles (fingerprint, build_target) live on their own tables (Wave 3) and
// are out of scope for this boundary schema.
import { z } from "zod";
import { AccountId, AppId, ConnectionId, EnvironmentId, FingerprintId } from "../ids.js";
import { MinorUnits } from "../money.js";
import { Timestamp } from "./common.js";
import { AppHost, AppIntent, AppOrigin, AppStage, AppStatus, AppType, AppVerdict, AutonomyLevel, CeilingSource, Cloud, ConnectionMode, ConnectionPlatform, ConnectionStatus, EnvironmentType, RepoHost, ShiftProfile, } from "./enums.js";
/** A customer app under management. */
export const App = z.object({
    id: AppId,
    accountId: AccountId,
    name: z.string().trim().min(1),
    type: AppType,
    intent: AppIntent,
    origin: AppOrigin,
    repoHost: RepoHost.nullable(),
    stage: AppStage,
    verdict: AppVerdict,
    host: AppHost,
    autonomyLevel: AutonomyLevel,
    /** Monthly per-app spend ceiling in EUR minor units (cents). Null when unset. */
    monthlyCeilingMinor: MinorUnits.nullable(),
    ceilingSource: CeilingSource,
    shiftProfile: ShiftProfile,
    /** The target customer cloud; null until the founder connects one (Slice 2, ADR-0016 §2). */
    cloud: Cloud.nullable(),
    /** The cloud region; null until set. */
    region: z.string().trim().min(1).nullable(),
    /** The current fingerprint row; null until the repo is fingerprinted (Slice 3). */
    fingerprintId: FingerprintId.nullable(),
    /** When the founder confirmed the route card; null until confirmed (ADR-0016 §1). */
    routeConfirmedAt: Timestamp.nullable(),
    status: AppStatus,
    parkedAt: Timestamp.nullable(),
    offboardedAt: Timestamp.nullable(),
    createdAt: Timestamp,
});
/**
 * The tenant-safe app projection the API returns to the founder SPA (portfolio + add-app). It OMITS
 * the spend fields (monthlyCeilingMinor, ceilingSource - D27: no cost surface to a customer path),
 * the Nightcrew-owned shiftProfile, and the lifecycle timestamps (parkedAt/offboardedAt, Slice 12),
 * and drops accountId (the caller's tenant is implicit). The producer is apps/api (app-mapper.ts);
 * every SPA read (list/detail/create) parses THIS, never the full `App`.
 */
export const AppView = z.object({
    id: AppId,
    name: z.string(),
    type: AppType,
    intent: AppIntent,
    origin: AppOrigin,
    repoHost: RepoHost.nullable(),
    stage: AppStage,
    verdict: AppVerdict,
    host: AppHost,
    autonomyLevel: AutonomyLevel,
    cloud: Cloud.nullable(),
    region: z.string().nullable(),
    fingerprintId: FingerprintId.nullable(),
    routeConfirmedAt: Timestamp.nullable(),
    status: AppStatus,
    createdAt: Timestamp,
});
/** A federated or scoped-token connection to an external platform. Stores no long-lived key. */
export const Connection = z.object({
    id: ConnectionId,
    accountId: AccountId,
    platform: ConnectionPlatform,
    mode: ConnectionMode,
    status: ConnectionStatus,
    lastSyncedAt: Timestamp.nullable(),
    createdAt: Timestamp,
});
/** A deployment environment for an app. `required` is set by the fingerprint floor. */
export const Environment = z.object({
    id: EnvironmentId,
    appId: AppId,
    type: EnvironmentType,
    enabled: z.boolean(),
    required: z.boolean(),
});
//# sourceMappingURL=apps.js.map